import { FormEvent, useEffect, useState } from "react";
import { getPublicCompany, submitContact, PublicCompany } from "../../lib/publicApi";
import { useToast } from "../../components/Toast";
import StarField from "../../components/StarField";

export default function Contact() {
  const toast = useToast();
  const [company, setCompany] = useState<PublicCompany | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    getPublicCompany().then(setCompany).catch(() => {});
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact({ name, email, phone: phone || undefined, subject: subject || undefined, message });
      setSent(true);
      toast.show("success", "Zpráva odeslána. Ozveme se co nejdříve.");
    } catch (err: any) {
      toast.show("error", err.message ?? "Odeslání selhalo.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-obloha-indigo-950 to-obloha-indigo-900 py-24">
        <StarField density={0.00005} />
        <div className="pointer-events-none absolute left-1/3 top-1/2 h-96 w-96 rounded-full bg-purple-500/15 blur-3xl" />
        <div className="pointer-events-none absolute right-1/3 top-1/4 h-96 w-96 rounded-full bg-obloha-gold-500/15 blur-3xl" />

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="mb-4 inline-block rounded-full border border-obloha-gold-400/30 bg-obloha-gold-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider text-obloha-gold-400">
            Spojte se s námi
          </div>
          <h1 className="text-5xl font-bold md:text-6xl">
            <span className="bg-gradient-to-b from-white to-obloha-indigo-200 bg-clip-text text-transparent">
              Kontakt
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-200">
            Máte dotaz? Potřebujete individuální termín nebo netypický počet dětí? Napište nám.
          </p>
        </div>
      </section>

      <section className="bg-obloha-indigo-950 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 lg:grid-cols-5">
            {/* Formulář */}
            <div className="lg:col-span-3">
              {sent ? (
                <div className="rounded-3xl border border-emerald-400/30 bg-emerald-500/10 p-10 text-center">
                  <div className="text-5xl">✅</div>
                  <h2 className="mt-4 text-2xl font-bold">Zpráva odeslána</h2>
                  <p className="mt-2 text-slate-300">
                    Děkujeme, ozveme se vám co nejdříve (nejpozději do pár pracovních dní).
                  </p>
                  <button
                    onClick={() => {
                      setSent(false);
                      setName("");
                      setEmail("");
                      setPhone("");
                      setSubject("");
                      setMessage("");
                    }}
                    className="mt-6 rounded-full bg-obloha-gold-500 px-6 py-2 font-semibold text-obloha-indigo-950 hover:bg-obloha-gold-400"
                  >
                    Poslat další zprávu
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur"
                >
                  <h2 className="text-2xl font-bold">Napište nám</h2>
                  <p className="mt-1 text-sm text-slate-300">* povinná pole</p>

                  <div className="mt-6 grid gap-4 md:grid-cols-2">
                    <Field label="Vaše jméno *">
                      <input
                        className="input-dark"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </Field>
                    <Field label="E-mail *">
                      <input
                        type="email"
                        className="input-dark"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Field>
                    <Field label="Telefon">
                      <input
                        type="tel"
                        className="input-dark"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Field>
                    <Field label="Předmět">
                      <input
                        className="input-dark"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Např. Dotaz na termín"
                      />
                    </Field>
                  </div>
                  <Field label="Vaše zpráva *" className="mt-4">
                    <textarea
                      className="input-dark min-h-32"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </Field>

                  <div className="mt-6">
                    <button
                      type="submit"
                      disabled={sending}
                      className="rounded-full bg-gradient-to-r from-obloha-gold-500 to-obloha-gold-400 px-8 py-3 font-semibold text-obloha-indigo-950 shadow-lg shadow-obloha-gold-500/30 transition-all hover:scale-105 disabled:opacity-60"
                    >
                      {sending ? "Odesílám…" : "Odeslat zprávu"}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-2">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                <h3 className="text-xl font-bold">Kontaktní údaje</h3>
                {company ? (
                  <div className="mt-6 space-y-4 text-sm">
                    <InfoItem icon="🏢" label="Firma">
                      {company.name}
                      {company.ico && (
                        <div className="text-xs text-slate-400">IČO: {company.ico}</div>
                      )}
                    </InfoItem>
                    {company.street && (
                      <InfoItem icon="📍" label="Sídlo">
                        {company.street}
                        <br />
                        {company.zip} {company.city}
                      </InfoItem>
                    )}
                    {company.email && (
                      <InfoItem icon="✉️" label="E-mail">
                        <a
                          href={`mailto:${company.email}`}
                          className="text-obloha-gold-400 hover:underline"
                        >
                          {company.email}
                        </a>
                      </InfoItem>
                    )}
                    {company.phone && (
                      <InfoItem icon="📞" label="Telefon">
                        <a
                          href={`tel:${company.phone}`}
                          className="text-obloha-gold-400 hover:underline"
                        >
                          {company.phone}
                        </a>
                      </InfoItem>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 text-slate-400">Načítám…</div>
                )}
              </div>

              <div className="mt-6 rounded-3xl border border-obloha-gold-400/30 bg-gradient-to-br from-obloha-gold-500/10 to-transparent p-8">
                <div className="text-4xl">⏱</div>
                <h3 className="mt-3 text-lg font-semibold">Reagujeme rychle</h3>
                <p className="mt-2 text-sm text-slate-300">
                  Na e-maily odpovídáme zpravidla do 24 hodin. V pracovních dnech často i dříve.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className ?? ""}`}>
      <span className="mb-1 block text-sm font-medium text-slate-200">{label}</span>
      {children}
    </label>
  );
}

function InfoItem({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="text-xl">{icon}</div>
      <div>
        <div className="text-xs uppercase text-slate-400">{label}</div>
        <div className="mt-0.5 text-slate-200">{children}</div>
      </div>
    </div>
  );
}
